<template>
  <div class="product-categories-container">
    <!-- 顶部标题区域 -->
    <div class="top-title-card">
      <h2 class="page-main-title">商户分类管理</h2>
      <p class="title-desc">管理商户分类层级，支持一级/二级/三级分类创建、编辑、删除与排序调整</p>
    </div>

    <!-- 主体内容区域 -->
    <div class="card-layout">
      <!-- 操作面板 -->
      <div class="operation-card">
        <div class="card-title">操作面板</div>
        <div class="operation-panel">
          <el-button
              type="primary"
              icon="el-icon-plus"
              class="panel-btn"
              @click="openAddDialog('1')"
          >
            新增一级
          </el-button>
          <el-button
              type="default"
              icon="el-icon-fold"
              class="panel-btn"
              @click="handleCollapseAll"
          >
            全部折叠
          </el-button>

          <el-button type="success" icon="el-icon-refresh" @click="getMerchantCategories" class="panel-btn">
            刷新列表
          </el-button>
          <el-button
              :type="sorting ? 'warning' : 'info'"
              icon="el-icon-s-tools"
              @click="sorting ? cancelSort : startSort"
              class="panel-btn"
              :disabled="!hasSortPermission"
          >
            {{ sorting ? '取消排序' : '编辑排序' }}
          </el-button>
          <el-button
              type="danger"
              icon="el-icon-delete"
              @click="batchDelete"
              :disabled="selectedIds.length === 0 || !hasDeletePermission"
              class="panel-btn"
          >
            批量删除 ({{ selectedIds.length }})
          </el-button>
        </div>
        <div class="operation-tips">
          <i class="el-icon-info"></i>
          <span>提示：删除上级分类会同时删除其下所有子分类</span>
        </div>
      </div>

      <!-- 分类列表区域 -->
      <div class="category-list-card">
        <div class="card-title">商户分类列表（树形）</div>

        <!-- 空数据状态 -->
        <div v-if="categories.length === 0" class="empty-card">
          <el-empty description="暂无分类数据" :image-size="100">
            <el-button type="primary" @click="openAddDialog('1')">立即新增</el-button>
          </el-empty>
        </div>

        <!-- 树形列表 -->
        <div v-else class="category-tree-wrapper">
          <div class="tree-container">
            <el-tree
                ref="categoryTree"
                :data="categories"
                :props="treeProps"
                :expand-on-click-node="false"
                :default-expand-all="false"
                class="custom-tree"
                :indent="24"
                :disabled="sorting"
                node-key="id"
                :load="loadChildNodes"
                :lazy="false"
            >
              <template #default="{ node, data }">
                <div class="tree-node-content" :data-level="data.level">
                  <!-- 复选框 -->
                  <el-checkbox
                      v-model="data.checked"
                      @change="handleNodeCheck(data)"
                      class="node-checkbox"
                      :disabled="sorting || !hasDeletePermission"
                  ></el-checkbox>

                  <!-- 节点内容区域 -->
                  <div class="node-content-wrapper">
                    <!-- 基本信息行 -->
                    <div class="node-basic-info">
                      <!-- 图标显示 -->
                      <div class="node-icon-wrapper" v-if="data.iconId || getIconById(data.iconId)">
                        <div class="icon-placeholder">
                          <img
                              :src="getIconUrl(data.iconId)"
                              :alt="getIconById(data.iconId)?.iconChar || '📷'"
                              class="category-icon"
                              @error="handleIconError($event, data.iconId)"
                          >
                          <!-- 白板框备用显示 -->
                          <span class="icon-fallback" v-if="!data.iconId || !getIconUrl(data.iconId)">{{ getIconById(data.iconId)?.iconChar || '📷' }}</span>
                        </div>
                      </div>
                      <span class="node-name">{{ data.name }}</span>
                      <div class="node-meta-info">
                        <span class="meta-item">ID: {{ data.id }}</span>
                        <span class="meta-item">排序：{{ data.sort }}</span>
                        <span class="meta-item" v-if="data.merchantCount !== undefined">商户数：{{ data.merchantCount }}</span>
                        <span class="meta-item level-tag">
                          {{ data.level === 1 ? '一级' : data.level === 2 ? '二级' : '三级' }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- 操作按钮区域 -->
                  <div class="node-actions">
                    <el-button
                        size="mini"
                        type="text"
                        icon="el-icon-edit"
                        @click="openEditDialog(data)"
                        :disabled="sorting || !hasEditPermission"
                    >
                      编辑
                    </el-button>
                    <el-button
                        size="mini"
                        type="text"
                        icon="el-icon-plus"
                        @click="openAddChildDialog(data)"
                        :disabled="data.level === 3 || sorting || !hasAddPermission"
                        class="add-child-btn"
                    >
                      添加子分类
                    </el-button>
                    <el-button
                        size="mini"
                        type="text"
                        icon="el-icon-delete"
                        @click="deleteCategory(data.id)"
                        :disabled="sorting || !hasDeletePermission"
                        class="text-danger delete-btn"
                    >
                      删除
                    </el-button>
                  </div>
                </div>
              </template>
            </el-tree>

            <!-- 排序完成按钮 -->
            <div v-if="sorting" class="sort-actions">
              <el-button type="primary" @click="finishSort" class="btn-lg">完成排序</el-button>
              <el-button @click="cancelSort" class="btn-lg">取消</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分类编辑/新增弹窗 - 核心修改：添加custom-class + 调整遮罩层挂载位置 -->
    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="600px"
        :close-on-click-modal="false"
        class="product-category-dialog"
        custom-class="category-edit-dialog"
        :modal-append-to-body="false"
        :append-to-body="true"
        @closed="handleDialogClosed"
    >
      <el-form ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-width="100px" class="dialog-form">
        <el-form-item label="分类级别" prop="level">
          <el-select v-model="categoryForm.level" disabled class="form-control">
            <el-option label="一级分类" value="1"></el-option>
            <el-option label="二级分类" value="2"></el-option>
            <el-option label="三级分类" value="3"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="上级分类" prop="parentId" v-if="categoryForm.level !== '1'">
          <el-select v-model="categoryForm.parentId" placeholder="请选择上级分类" clearable class="form-control">
            <el-option
                v-for="item in getParentOptions(categoryForm.level)"
                :key="item.id"
                :label="item.name"
                :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="categoryForm.name" placeholder="请输入分类名称" maxlength="50" show-word-limit
                    class="form-control"></el-input>
        </el-form-item>
        <el-form-item label="分类图标" prop="iconId">
          <div class="icon-select-wrapper">
            <!-- 当前选中的图标预览 -->
            <div class="icon-preview" v-if="selectedIcon">
              <div class="preview-icon-placeholder">
                <img :src="selectedIcon.url" :alt="selectedIcon.iconChar || selectedIcon.name" class="selected-icon" @error="handleIconError">
                <span class="preview-fallback" v-if="!selectedIcon.url">{{ selectedIcon.iconChar || '📷' }}</span>
              </div>
              <span class="icon-name">{{ selectedIcon.name }}</span>
            </div>

            <!-- 选择图标按钮 -->
            <el-button
                type="primary"
                icon="el-icon-picture"
                @click="openIconSelector"
                class="select-icon-btn"
            >
              {{ selectedIcon ? '更换图标' : '选择图标' }}
            </el-button>

            <!-- 清除图标按钮 -->
            <el-button
                v-if="selectedIcon"
                type="text"
                @click="clearIcon"
                class="clear-icon-btn"
            >
              清除
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="分类排序" prop="sort">
          <el-input-number v-model="categoryForm.sort" :min="0" :max="999" placeholder="数字越小越靠前"
                           class="form-control"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" class="btn-default">取消</el-button>
        <el-button type="primary" @click="submitCategoryForm" class="btn-primary" :loading="formSubmitting">提交</el-button>
      </div>
    </el-dialog>

    <!-- 图标选择器弹窗 - 同步修改 -->
    <el-dialog
        title="选择图标"
        :visible.sync="iconSelectorVisible"
        width="800px"
        :close-on-click-modal="false"
        class="icon-selector-dialog"
        custom-class="icon-select-dialog"
        :modal-append-to-body="false"
        :append-to-body="true"
        @closed="handleIconDialogClosed"
    >
      <div class="icon-selector-content">
        <!-- 搜索框 -->
        <div class="icon-search">
          <el-input
              v-model="iconSearchText"
              placeholder="搜索图标名称/ID/分类"
              clearable
              prefix-icon="el-icon-search"
              @input="filterIcons"
          />
        </div>

        <!-- 图标列表 -->
        <div class="icon-list-container">
          <div v-if="filteredIcons.length === 0" class="empty-icons">
            <el-empty description="未找到相关图标" :image-size="80" />
          </div>

          <div v-else class="icon-grid">
            <div
                v-for="icon in filteredIcons"
                :key="icon.id"
                :class="['icon-item', { 'selected': tempSelectedIcon && tempSelectedIcon.id === icon.id }]"
                @click="selectIcon(icon)"
            >
              <div class="icon-image-wrapper">
                <img :src="icon.url" :alt="icon.iconChar || icon.name" @error="handleIconError($event, icon.id)">
                <!-- 白板框备用显示emoji -->
                <span class="icon-char-fallback">{{ icon.iconChar || '📷' }}</span>
              </div>
              <div class="icon-info">
                <div class="icon-name">{{ icon.name }}</div>
                <div class="icon-id">ID: {{ icon.id }}</div>
                <div class="icon-category">{{ icon.mainCategory }} / {{ icon.subCategory }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="iconSelectorVisible = false" class="btn-default">取消</el-button>
        <el-button type="primary" @click="confirmIconSelection" class="btn-primary">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api";
import sortablejs from 'sortablejs';
import { debounce } from 'lodash';

export default {
  name: "MerchantCategories",
  data() {
    return {
      categories: [],
      selectedIds: [],
      dialogVisible: false,
      dialogType: "add",
      sorting: false,
      sortingList: [],
      selectedIcon: null,
      formSubmitting: false,
      iconLoading: false,

      // 图标选择器相关
      iconSelectorVisible: false,
      iconList: [],
      filteredIcons: [],
      iconSearchText: "",
      tempSelectedIcon: null,

      categoryForm: {
        id: "",
        level: "1",
        parentId: "",
        name: "",
        sort: 0,
        iconId: ""
      },

      // 表单校验规则
      categoryRules: {
        name: [{ required: true, message: "请输入分类名称", trigger: "blur" }],
        sort: [{ required: true, message: "请输入分类排序", trigger: "blur", type: 'number' }],
        parentId: [{ required: true, message: "请选择上级分类", trigger: "change" }]
      },

      // 树形控件配置
      treeProps: {
        children: 'children',
        label: 'name'
      },

      // 权限控制
      permission: {
        add: true,
        edit: true,
        delete: true,
        sort: true
      },

      // 防重复点击
      isOpeningDialog: false
    };
  },
  computed: {
    // 弹窗标题
    dialogTitle() {
      const levelText = {
        '1': '一级',
        '2': '二级',
        '3': '三级'
      }[this.categoryForm.level];
      return this.dialogType === "add" ? `新增${levelText}分类` : `编辑${levelText}分类`;
    },

    // 权限计算属性
    hasAddPermission() {
      return this.permission.add;
    },
    hasEditPermission() {
      return this.permission.edit;
    },
    hasDeletePermission() {
      return this.permission.delete;
    },
    hasSortPermission() {
      return this.permission.sort;
    }
  },
  watch: {
    // 弹窗打开后修复层级
    dialogVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.fixModalLayer();
          }, 10);
        });
      }
    },

    iconSelectorVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.fixModalLayer();
          }, 10);
        });
      }
    }
  },
  created() {
    this.filterIcons = debounce(this.filterIcons, 300);
    this.getPermissions();
  },
  mounted() {
    this.getMerchantCategories();
    this.loadAllIcons();

    // 确保弹窗正确显示
    this.$nextTick(() => {
      this.ensureDialogLayer();
    });
  },
  methods: {
    /**
     * 修复弹窗z-index层级
     */
    fixDialogZIndex() {
      this.$nextTick(() => {
        // 查找所有弹窗相关元素
        const dialogs = document.querySelectorAll('.el-dialog');
        const modals = document.querySelectorAll('.v-modal');

        // 设置弹窗层级
        dialogs.forEach(dialog => {
          if (dialog) {
            dialog.style.zIndex = '10002';
            dialog.style.position = 'relative';
          }
        });

        // 设置遮罩层层级（低于弹窗）
        modals.forEach(modal => {
          if (modal) {
            modal.style.zIndex = '10001';
          }
        });
      });
    },

    /**
     * 确保弹窗层级的最终解决方案
     */
    ensureDialogLayer() {
      // 创建一个样式元素
      const style = document.createElement('style');
      style.innerHTML = `
        /* 强制修复弹窗层级 */
        .el-dialog__wrapper {
          z-index: 2000 !important;
        }
        .el-dialog {
          z-index: 2001 !important;
          position: relative !important;
        }
        .v-modal {
          z-index: 1999 !important;
        }
        /* 特殊处理分类弹窗 */
        .product-category-dialog .el-dialog {
          z-index: 3001 !important;
        }
        .product-category-dialog .v-modal {
          z-index: 3000 !important;
        }
        /* 特殊处理图标选择器 */
        .icon-selector-dialog .el-dialog {
          z-index: 3101 !important;
        }
        .icon-selector-dialog .v-modal {
          z-index: 3100 !important;
        }
      `;
      document.head.appendChild(style);
    },

    /**
     * 修复弹窗遮罩层层级
     */
    fixModalLayer() {
      this.$nextTick(() => {
        // 编辑弹窗遮罩层
        const categoryModal = document.querySelector('.category-edit-dialog + .v-modal');
        // 图标弹窗遮罩层
        const iconModal = document.querySelector('.icon-select-dialog + .v-modal');

        // 编辑弹窗遮罩层层级低于弹窗
        if (categoryModal) {
          categoryModal.style.zIndex = '3000';
          categoryModal.style.opacity = '0.5';
        }
        // 图标弹窗遮罩层层级低于图标弹窗
        if (iconModal) {
          iconModal.style.zIndex = '3100';
          iconModal.style.opacity = '0.5';
        }

        // 确保弹窗本身层级更高
        const categoryDialog = document.querySelector('.category-edit-dialog');
        const iconDialog = document.querySelector('.icon-select-dialog');

        if (categoryDialog) {
          categoryDialog.style.zIndex = '3001';
        }
        if (iconDialog) {
          iconDialog.style.zIndex = '3101';
        }
      });
    },

    /**
     * 获取用户权限
     */
    getPermissions() {
      this.permission = {
        add: true,
        edit: true,
        delete: true,
        sort: true
      };
    },

    /**
     * 加载所有图标
     */
    loadAllIcons() {
      if (this.iconList.length > 0) {
        this.filteredIcons = [...this.iconList];
        return;
      }

      this.iconLoading = true;
      api.industryIcon_getAllIcons({})
          .then(res => {
            this.iconLoading = false;
            if (res.data.code === '000' && res.data.success) {
              const iconData = res.data.data;
              let icons = [];

              if (iconData && iconData.list) {
                icons = iconData.list;
              } else if (Array.isArray(iconData)) {
                icons = iconData;
              } else {
                icons = [];
              }

              this.iconList = icons.map(icon => ({
                ...icon,
                name: icon.iconName || icon.industryName || `图标${icon.id}`,
                url: icon.url || '',
                iconChar: icon.iconChar || '📷'
              }));
              this.filteredIcons = [...this.iconList];
            }
          })
          .catch(err => {
            this.iconLoading = false;
            console.error('加载图标列表失败：', err);
            this.$message.error('加载图标列表失败');
          });
    },

    /**
     * 根据图标ID获取图标信息
     */
    getIconById(iconId) {
      if (!iconId || !Array.isArray(this.iconList)) return null;
      const id = Number(iconId);
      const icon = this.iconList.find(item => item.id === id);
      return icon || null;
    },

    /**
     * 获取图标URL
     */
    getIconUrl(iconId) {
      const icon = this.getIconById(iconId);
      return icon?.url || '';
    },

    /**
     * 过滤图标
     */
    filterIcons() {
      if (!Array.isArray(this.iconList)) {
        this.filteredIcons = [];
        return;
      }

      if (!this.iconSearchText.trim()) {
        this.filteredIcons = [...this.iconList];
        return;
      }

      const searchText = this.iconSearchText.toLowerCase().trim();
      this.filteredIcons = this.iconList.filter(icon => {
        const idMatch = icon.id && icon.id.toString().includes(searchText);
        const nameMatch = icon.iconName && icon.iconName.toLowerCase().includes(searchText);
        const industryMatch = icon.industryName && icon.industryName.toLowerCase().includes(searchText);
        const mainCategoryMatch = icon.mainCategory && icon.mainCategory.toLowerCase().includes(searchText);
        const subCategoryMatch = icon.subCategory && icon.subCategory.toLowerCase().includes(searchText);

        return idMatch || nameMatch || industryMatch || mainCategoryMatch || subCategoryMatch;
      });
    },

    /**
     * 全部折叠
     */
    handleCollapseAll() {
      this.$nextTick(() => {
        const treeRef = this.$refs.categoryTree;
        if (!treeRef) {
          this.$message.warning("树形组件未加载完成");
          return;
        }

        try {
          const expandIcons = treeRef.$el.querySelectorAll('.el-tree-node__expand-icon.expanded');
          expandIcons.forEach(icon => {
            icon.click();
          });
          this.$message.success("已全部折叠");
        } catch (e) {
          console.error("折叠失败：", e);
          this.$message.error("折叠操作失败，请重试");
        }
      });
    },

    /**
     * 获取商户分类列表
     */
    getMerchantCategories() {
      this.sorting = false;
      const loading = this.$loading({
        lock: true,
        text: '加载分类数据中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      api.merchantCategory_getMerchantCategories({
        countMerchant: true
      }).then(res => {
        loading.close();
        if (res?.data?.code === '000' && res.data.success) {
          const addCheckedProp = (list) => {
            return list.map(item => {
              const newItem = {
                ...item,
                checked: false
              };
              if (item.children && item.children.length) {
                newItem.children = addCheckedProp(item.children);
              }
              return newItem;
            });
          };
          this.categories = addCheckedProp(res.data.data || []);
          this.updateSelectedIds();
        } else {
          this.$message.error(res?.data?.message || '获取分类数据失败');
        }
      }).catch(err => {
        loading.close();
        console.error('获取分类数据出错：', err);
        this.$message.error(`获取分类数据失败：${err.message || '请重试'}`);
      });
    },

    /**
     * 图标加载失败处理
     */
    handleIconError(event, iconId) {
      event.target.style.display = 'none';
      const fallbackEl = event.target.nextElementSibling;
      if (fallbackEl) {
        fallbackEl.style.display = 'flex';
      }
    },

    /**
     * 获取上级分类选项
     */
    getParentOptions(currentLevel) {
      if (!currentLevel) return [];

      const parentLevel = parseInt(currentLevel) - 1;
      if (isNaN(parentLevel) || parentLevel < 1) return [];

      const options = [];
      const collectOptions = (list) => {
        if (!Array.isArray(list)) return;

        list.forEach(item => {
          if (item.level === parentLevel) {
            options.push(item);
          }
          if (item.children && item.children.length) {
            collectOptions(item.children);
          }
        });
      };
      collectOptions(this.categories);
      return options;
    },

    /**
     * 处理节点复选框选择
     */
    handleNodeCheck(data) {
      if (!data) return;

      const setChildrenChecked = (node, checked) => {
        if (node?.children && node.children.length) {
          node.children.forEach(child => {
            child.checked = checked;
            setChildrenChecked(child, checked);
          });
        }
      };

      const checkParentChecked = (node) => {
        if (!node || !node.parentId) return;

        const findParent = (list, parentId) => {
          for (let item of list) {
            if (item.id === parentId) return item;
            if (item.children) {
              const found = findParent(item.children, parentId);
              if (found) return found;
            }
          }
          return null;
        };

        const parent = findParent(this.categories, node.parentId);
        if (parent) {
          const allChecked = parent.children?.every(child => child.checked) || false;
          const allUnchecked = parent.children?.every(child => !child.checked) || true;

          parent.checked = allChecked ? true : (allUnchecked ? false : parent.checked);
          checkParentChecked(parent);
        }
      };

      setChildrenChecked(data, data.checked);
      checkParentChecked(data);
      this.updateSelectedIds();
    },

    /**
     * 更新选中的ID列表
     */
    updateSelectedIds() {
      const ids = [];
      const collectIds = (list) => {
        if (!Array.isArray(list)) return;

        list.forEach(item => {
          if (item.checked) ids.push(item.id);
          if (item.children && item.children.length) {
            collectIds(item.children);
          }
        });
      };
      collectIds(this.categories);
      this.selectedIds = [...new Set(ids)];
    },

    /**
     * 打开新增分类弹窗
     */
    openAddDialog(level = "1", parentId = "") {
      if (!this.hasAddPermission) {
        this.$message.warning('您没有新增分类的权限');
        return;
      }

      if (this.isOpeningDialog) return;
      this.isOpeningDialog = true;

      this.dialogType = "add";
      this.categoryForm = {
        id: "",
        level: level,
        parentId: parentId || "",
        name: "",
        sort: 0,
        iconId: ""
      };
      this.selectedIcon = null;

      this.$nextTick(() => {
        if (this.$refs.categoryForm) {
          this.$refs.categoryForm.clearValidate();
        }
        this.dialogVisible = true;
        this.isOpeningDialog = false;
        // 修复遮罩层
        setTimeout(() => {
          this.fixModalLayer();
        }, 0);
      });
    },

    /**
     * 打开添加子分类弹窗
     */
    openAddChildDialog(data) {
      if (!this.hasAddPermission) {
        this.$message.warning('您没有新增分类的权限');
        return;
      }

      const childLevel = parseInt(data.level) + 1;
      if (childLevel > 3) {
        this.$message.warning("最多支持三级分类");
        return;
      }
      this.openAddDialog(childLevel.toString(), data.id);
    },

    /**
     * 打开编辑分类弹窗
     */
    openEditDialog(data) {
      console.log('打开编辑弹窗，节点数据:', data);

      if (!this.hasEditPermission) {
        this.$message.warning('您没有编辑分类的权限');
        return;
      }

      if (!data || !data.id) {
        this.$message.error('分类数据异常，无法编辑');
        return;
      }

      this.dialogType = "edit";

      this.categoryForm = {
        id: data.id.toString(),
        level: data.level.toString(),
        parentId: data.parentId ? data.parentId.toString() : "",
        name: data.name || '',
        sort: data.sort || 0,
        iconId: data.iconId ? data.iconId.toString() : ""
      };

      console.log('表单数据已设置:', this.categoryForm);

      if (data.iconId) {
        const icon = this.getIconById(data.iconId);
        if (icon) {
          this.selectedIcon = {
            id: icon.id,
            name: icon.iconName || icon.industryName || '未知图标',
            url: this.getIconUrl(icon.id),
            iconChar: icon.iconChar
          };
        } else {
          this.selectedIcon = null;
        }
      } else {
        this.selectedIcon = null;
      }

      this.$nextTick(() => {
        if (this.$refs.categoryForm) {
          this.$refs.categoryForm.clearValidate();
        }
        this.dialogVisible = true;
        // 修复遮罩层
        setTimeout(() => {
          this.fixModalLayer();
        }, 0);
      });
    },

    /**
     * 弹窗关闭后清理
     */
    handleDialogClosed() {
      this.categoryForm = {
        id: "",
        level: "1",
        parentId: "",
        name: "",
        sort: 0,
        iconId: ""
      };
      this.selectedIcon = null;

      if (this.$refs.categoryForm) {
        this.$refs.categoryForm.clearValidate();
      }
    },

    /**
     * 打开图标选择器
     */
    openIconSelector() {
      this.iconSelectorVisible = true;
      this.iconSearchText = "";
      this.filteredIcons = [...this.iconList];
      this.tempSelectedIcon = this.selectedIcon;
      // 修复遮罩层
      setTimeout(() => {
        this.fixModalLayer();
      }, 0);
    },

    /**
     * 选择图标
     */
    selectIcon(icon) {
      if (!icon) return;

      this.tempSelectedIcon = {
        id: icon.id,
        name: icon.iconName || icon.industryName || '未知图标',
        url: this.getIconUrl(icon.id),
        iconChar: icon.iconChar,
        mainCategory: icon.mainCategory,
        subCategory: icon.subCategory
      };
    },

    /**
     * 确认图标选择
     */
    confirmIconSelection() {
      if (this.tempSelectedIcon) {
        this.selectedIcon = { ...this.tempSelectedIcon };
        this.categoryForm.iconId = this.selectedIcon.id.toString();
      } else {
        this.selectedIcon = null;
        this.categoryForm.iconId = "";
      }
      this.iconSelectorVisible = false;
    },

    /**
     * 图标弹窗关闭后清理
     */
    handleIconDialogClosed() {
      this.tempSelectedIcon = null;
      this.iconSearchText = "";
    },

    /**
     * 清除选中的图标
     */
    clearIcon() {
      this.selectedIcon = null;
      this.categoryForm.iconId = "";
    },

    /**
     * 提交分类表单
     */
    submitCategoryForm() {
      if (this.formSubmitting) return;

      this.$refs.categoryForm.validate((valid) => {
        if (valid) {
          this.formSubmitting = true;

          const submitData = {};

          if (this.dialogType === "edit") {
            submitData.id = Number(this.categoryForm.id);
          }

          submitData.name = this.categoryForm.name;
          submitData.sort = Number(this.categoryForm.sort);
          submitData.level = parseInt(this.categoryForm.level);

          if (this.categoryForm.parentId) {
            submitData.parentId = Number(this.categoryForm.parentId);
          }
          if (this.categoryForm.iconId) {
            submitData.iconId = Number(this.categoryForm.iconId);
          }

          let apiPromise;

          if (this.dialogType === "add") {
            apiPromise = api.merchantCategory_addMerchantCategory(submitData);
          } else {
            apiPromise = api.merchantCategory_editMerchantCategory(submitData);
          }

          apiPromise.then(res => {
            this.formSubmitting = false;
            if (res?.data?.code !== '000') {
              this.$message.error(res?.data?.message || '操作失败');
              return;
            }

            const actionText = this.dialogType === "add" ? "新增" : "编辑";
            this.$message.success(`${actionText}分类成功`);
            this.dialogVisible = false;
            this.getMerchantCategories();

          }).catch(err => {
            this.formSubmitting = false;
            console.error(`${this.dialogType === "add" ? "新增" : "编辑"}分类失败：`, err);
            this.$message.error(`${this.dialogType === "add" ? "新增" : "编辑"}分类失败：${err.message || '请重试'}`);
          });
        }
      });
    },

    /**
     * 删除单个分类
     */
    deleteCategory(id) {
      if (!this.hasDeletePermission) {
        this.$message.warning('您没有删除分类的权限');
        return;
      }

      if (!id) return;

      this.$confirm("确定删除该分类吗？删除后会同时删除其下所有子分类！", "警告", {
        type: "warning",
        confirmButtonText: "确定删除",
        cancelButtonText: "取消"
      }).then(() => {
        api.merchantCategory_deleteMerchantCategory({
          id: id
        }).then(res => {
          if (res?.data?.code !== '000') {
            this.$message.error(res?.data?.message || '删除失败');
            return;
          }
          this.$message.success("分类删除成功");
          this.getMerchantCategories();
          this.updateSelectedIds();
        }).catch(err => {
          console.error('删除分类失败：', err);
          this.$message.error(`删除分类失败：${err.message || '请重试'}`);
        });
      }).catch(() => {
        // 用户取消删除
      });
    },

    /**
     * 批量删除分类
     */
    batchDelete() {
      if (!this.hasDeletePermission) {
        this.$message.warning('您没有删除分类的权限');
        return;
      }

      if (this.selectedIds.length === 0) {
        this.$message.warning("请选择要删除的分类");
        return;
      }

      this.$confirm(`确定删除选中的${this.selectedIds.length}个分类吗？删除后会同时删除其下所有子分类！`, "警告", {
        type: "warning",
        confirmButtonText: "确定删除",
        cancelButtonText: "取消"
      }).then(() => {
        const loading = this.$loading({ text: '批量删除中...' });

        api.merchantCategory_batchDeleteMerchantCategory({
          ids: this.selectedIds
        }).then(res => {
          loading.close();
          if (res?.data?.code !== '000') {
            this.$message.error(res?.data?.message || '批量删除失败');
            return;
          }
          this.$message.success(`批量删除${this.selectedIds.length}个分类成功`);
          this.getMerchantCategories();
          this.selectedIds = [];
        }).catch(err => {
          loading.close();
          console.error('批量删除分类失败：', err);
          this.$message.error(`批量删除分类失败：${err.message || '请重试'}`);
        });
      }).catch(() => {
        // 用户取消删除
      });
    },

    /**
     * 开始排序
     */
    startSort() {
      if (!this.hasSortPermission) {
        this.$message.warning('您没有排序分类的权限');
        return;
      }

      this.sorting = true;
      this.$nextTick(() => {
        this.sortingList = JSON.parse(JSON.stringify(this.categories));

        const treeEl = this.$refs.categoryTree?.$el;
        if (!treeEl) return;

        const tbodyList = treeEl.querySelectorAll('.el-tree-node__children tbody, .el-tree > tbody');

        tbodyList.forEach(tbody => {
          if (tbody) {
            tbody.style.cursor = 'move';
            tbody.style.userSelect = 'none';

            sortablejs.create(tbody, {
              animation: 150,
              handle: '.tree-node-content',
              ghostClass: 'sortable-ghost',
              chosenClass: 'sortable-chosen',
              onEnd: (e) => {
                const oldIndex = e.oldIndex;
                const newIndex = e.newIndex;

                const updateSort = (list, parentId = null) => {
                  if (!Array.isArray(list)) return false;

                  for (let i = 0; i < list.length; i++) {
                    if (parentId === null && i === 0) {
                      const temp = list[oldIndex];
                      list.splice(oldIndex, 1);
                      list.splice(newIndex, 0, temp);
                      list.forEach((item, index) => {
                        item.sort = index + 1;
                      });
                      return true;
                    }

                    if (list[i].children && list[i].children.length) {
                      const updated = updateSort(list[i].children, list[i].id);
                      if (updated) return true;
                    }
                  }
                  return false;
                };

                updateSort(this.sortingList);
              }
            });
          }
        });

        document.body.ondrop = e => {
          e.preventDefault();
          e.stopPropagation();
        };
      });
    },

    /**
     * 取消排序
     */
    cancelSort() {
      this.sorting = false;
      this.getMerchantCategories();
    },

    /**
     * 完成排序
     */
    finishSort() {
      const sortedIds = [];
      const collectSortedIds = (list) => {
        if (!Array.isArray(list)) return;

        list.forEach(item => {
          sortedIds.push(item.id);
          if (item.children && item.children.length) {
            collectSortedIds(item.children);
          }
        });
      };

      collectSortedIds(this.sortingList);

      if (sortedIds.length === 0) {
        this.$message.warning('没有可排序的分类');
        this.sorting = false;
        return;
      }

      const loading = this.$loading({ text: '保存排序中...' });

      api.merchantCategory_editSort({
        ids: sortedIds
      }).then(res => {
        loading.close();
        if (res?.data?.code !== '000') {
          this.$message.error(res?.data?.message || '排序失败');
          return;
        }
        this.$message.success('编辑排序成功');
        this.sorting = false;
        this.getMerchantCategories();
      }).catch(err => {
        loading.close();
        console.error('排序失败：', err);
        this.$message.error(`排序失败：${err.message || '请重试'}`);
        this.sorting = false;
      });
    },

    /**
     * 加载子节点
     */
    loadChildNodes(node, resolve) {
      setTimeout(() => {
        resolve([]);
      }, 100);
    }
  }
};
</script>

<style lang="scss" scoped>
.product-categories-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
  box-sizing: border-box;

  .top-title-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    .page-main-title {
      margin: 0 0 8px 0;
      font-size: 20px;
      color: #303133;
      font-weight: 600;
    }

    .title-desc {
      margin: 0;
      font-size: 14px;
      color: #909399;
      line-height: 1.5;
    }
  }

  .card-layout {
    display: flex;
    gap: 20px;
    flex-direction: column;

    @media (min-width: 992px) {
      flex-direction: row;
    }
  }

  .operation-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    width: 100%;

    @media (min-width: 992px) {
      width: 280px;
      flex-shrink: 0;
    }

    .card-title {
      padding: 16px 20px;
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      border-bottom: 1px solid #ebeef5;
      background: #fafbfc;
    }

    .operation-panel {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;
    }

    .panel-btn {
      width: 100%;
      height: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      border-radius: 6px;
      margin: 0;
      padding: 0 16px;
      box-sizing: border-box;
    }

    .operation-tips {
      padding: 0 20px 20px;
      font-size: 12px;
      color: #909399;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .category-list-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    flex: 1;

    .card-title {
      padding: 16px 20px;
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      border-bottom: 1px solid #ebeef5;
      background: #fafbfc;
    }

    .empty-card {
      padding: 60px 20px;
      text-align: center;
    }

    .category-tree-wrapper {
      padding: 20px;
      min-height: 500px;

      .sort-actions {
        margin-top: 20px;
        display: flex;
        gap: 12px;
        justify-content: center;

        .el-button {
          min-width: 120px;
        }
      }
    }
  }
}

// 树形组件样式
::v-deep .custom-tree {
  background: transparent;

  .el-tree-node {
    position: relative;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .el-tree-node__content {
      height: auto !important;
      min-height: 80px;
      padding: 0 !important;
      display: flex;
      align-items: stretch;

      &:hover {
        background-color: transparent !important;
      }
    }

    .el-tree-node__children {
      margin-left: 40px;
      position: relative;
      padding-left: 20px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 1px;
        background: #ebeef5;
      }

      .el-tree-node {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: -20px;
          top: 40px;
          width: 20px;
          height: 1px;
          background: #ebeef5;
        }
      }
    }

    .el-tree-node__expand-icon {
      position: absolute;
      left: -40px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border-radius: 4px;
      border: 1px solid #dcdfe6;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
      z-index: 1;

      &:hover {
        border-color: #409eff;
        color: #409eff;
      }

      &.is-leaf {
        visibility: hidden;
      }

      &.expanded {
        transform: translateY(-50%) rotate(90deg);
        background: #409eff;
        border-color: #409eff;
        color: white;
      }
    }

    &.is-disabled {
      .tree-node-content {
        opacity: 0.8;

        .node-actions {
          opacity: 0.5;
        }
      }
    }
  }
}

// 树形节点内容
.tree-node-content {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  transition: all 0.3s;
  position: relative;
  cursor: pointer;

  &[data-level="1"] {
    border-left: 4px solid #409eff;
  }

  &[data-level="2"] {
    border-left: 4px solid #67c23a;
  }

  &[data-level="3"] {
    border-left: 4px solid #e6a23c;
  }

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-color: #c0c4cc;
  }

  .node-checkbox {
    margin-top: 4px;
    flex-shrink: 0;

    ::v-deep .el-checkbox__inner {
      border-radius: 4px;
    }
  }

  .node-content-wrapper {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .node-basic-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;

      .node-icon-wrapper {
        flex-shrink: 0;

        .icon-placeholder {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid #e4e7ed;
          background: #f5f7fa;
          position: relative;

          .category-icon {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
          }

          .icon-fallback {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: none;
            align-items: center;
            justify-content: center;
            font-size: 16px;
          }
        }
      }

      .node-name {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }

      .node-meta-info {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;

        .meta-item {
          font-size: 12px;
          color: #909399;
          background: #f5f7fa;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .level-tag {
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 4px;
          background: #f0f9ff;
          color: #409eff;
          font-weight: 500;
        }
      }
    }
  }

  .node-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    align-items: flex-start;
    margin-left: auto;
    padding-left: 16px;

    .el-button {
      font-size: 12px;
      padding: 6px 12px;
      height: 30px;
      border-radius: 4px;
      white-space: nowrap;
      min-width: 70px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;

      i {
        margin-right: 4px;
        font-size: 12px;
      }

      &:first-child {
        background: #f0f9ff;
        border-color: #b3d8ff;
        color: #409eff;

        &:hover {
          background: #ecf5ff;
          border-color: #409eff;
        }
      }

      &:nth-child(2) {
        background: #f0f9eb;
        border-color: #c2e7b0;
        color: #67c23a;

        &:hover {
          background: #e1f3d8;
          border-color: #67c23a;
        }

        &.is-disabled {
          background: #f5f7fa;
          border-color: #e4e7ed;
          color: #c0c4cc;
          cursor: not-allowed;
        }
      }

      &:nth-child(3) {
        background: #fef0f0;
        border-color: #fbc4c4;
        color: #f56c6c;

        &:hover {
          background: #fde2e2;
          border-color: #f56c6c;
        }
      }

      &:hover:not(.is-disabled) {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      &.is-disabled {
        cursor: not-allowed;
        opacity: 0.6;

        &:hover {
          transform: none;
          box-shadow: none;
        }
      }
    }
  }
}

// 图标选择器样式
.icon-select-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.icon-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  min-width: 200px;

  .preview-icon-placeholder {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 1px solid #e4e7ed;
    background: white;
    position: relative;

    .selected-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .preview-fallback {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      align-items: center;
      justify-content: center;
      font-size: 16px;
    }
  }
}

.icon-name {
  font-size: 14px;
  color: #606266;
}

.select-icon-btn {
  flex-shrink: 0;
}

.clear-icon-btn {
  color: #f56c6c;
}

.clear-icon-btn:hover {
  color: #f78989;
}

// 图标选择器内容样式
.icon-selector-content {
  .icon-search {
    margin-bottom: 20px;
  }

  .icon-list-container {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 16px;
    background: #fafbfc;

    .empty-icons {
      padding: 40px 0;
    }

    .icon-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 12px;
    }

    .icon-item {
      border: 1px solid #e4e7ed;
      border-radius: 8px;
      padding: 12px;
      background: white;
      cursor: pointer;
      transition: all 0.3s;
      text-align: center;
      position: relative;

      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
        transform: translateY(-2px);
      }

      &.selected {
        border-color: #409eff;
        background: #f0f9ff;
        box-shadow: 0 2px 12px rgba(64, 158, 255, 0.2);

        &::after {
          content: '✓';
          position: absolute;
          top: 8px;
          right: 8px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #409eff;
          color: white;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .icon-image-wrapper {
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 8px;
        position: relative;
        border-radius: 6px;
        border: 1px solid #e4e7ed;
        background: #f5f7fa;

        img {
          max-width: 48px;
          max-height: 48px;
          object-fit: contain;
          display: block;
        }

        .icon-char-fallback {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: none;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }
      }

      .icon-info {
        .icon-name {
          font-size: 12px;
          color: #303133;
          font-weight: 500;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .icon-id {
          font-size: 11px;
          color: #909399;
        }

        .icon-category {
          font-size: 10px;
          color: #c0c4cc;
          margin-top: 4px;
        }
      }
    }
  }
}

// 响应式调整
@media (max-width: 768px) {
  .tree-node-content {
    flex-direction: column;
    padding: 16px;
    gap: 12px;

    .node-content-wrapper {
      width: 100%;

      .node-basic-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;

        .node-meta-info {
          width: 100%;
        }
      }
    }

    .node-actions {
      width: 100%;
      margin-left: 0;
      padding-left: 0;
      justify-content: flex-start;
      flex-wrap: wrap;

      .el-button {
        flex: 1;
        min-width: auto;
        max-width: calc(25% - 6px);
      }
    }
  }

  ::v-deep .custom-tree {
    .el-tree-node {
      .el-tree-node__children {
        margin-left: 20px;
        padding-left: 10px;
      }

      .el-tree-node__expand-icon {
        left: -30px;
      }
    }
  }

  .icon-selector-content {
    .icon-grid {
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    }
  }
}

// 对话框表单样式
.dialog-form {
  .el-form-item {
    margin-bottom: 18px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .el-button {
    min-width: 80px;
  }
}

// 空状态样式
.empty-card {
  ::v-deep .el-empty {
    .el-empty__description {
      margin-top: 12px;
    }

    .el-button {
      margin-top: 16px;
    }
  }
}
</style>

<!-- ================== 关键修复：弹窗层级和遮罩层修复 ================== -->
<style>
/* 1. 重置所有弹窗的默认层级 */
.el-dialog__wrapper {
  z-index: auto !important;
}

.el-dialog {
  position: relative !important;
}

.v-modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  opacity: 0.5 !important;
  background: #000 !important;
  z-index: 1000 !important;
}

/* 2. 编辑/新增弹窗专属样式 - 遮罩层不遮挡弹窗 */
.category-edit-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 3001 !important; /* 高于自身遮罩层 */
  margin: 0 !important;
}

/* 编辑弹窗遮罩层 */
.category-edit-dialog + .v-modal {
  z-index: 3000 !important; /* 低于弹窗 */
  opacity: 0.5 !important;
}

/* 3. 图标选择器弹窗专属样式 */
.icon-select-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 3101 !important; /* 高于自身遮罩层 */
  margin: 0 !important;
}

/* 图标弹窗遮罩层 */
.icon-select-dialog + .v-modal {
  z-index: 3100 !important; /* 低于弹窗 */
  opacity: 0.5 !important;
}

/* 4. 保留删除确认框的遮罩层正常 */
.el-message-box__wrapper {
  z-index: 4000 !important;
}
.el-message-box {
  z-index: 4001 !important;
}
.el-message-box + .v-modal {
  z-index: 3999 !important;
}

/* 5. 其他层级重置（确保不冲突） */
.el-popper,
.el-select-dropdown,
.el-dropdown-menu,
.el-tooltip__popper {
  z-index: 9999 !important;
}

/* 6. 强制弹窗显示在最上层 */
body > .el-dialog__wrapper {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 9998 !important;
}

/* 7. 图片加载失败样式 */
.icon-image-wrapper img[src=""] + .icon-char-fallback,
.icon-image-wrapper img[src="null"] + .icon-char-fallback,
.icon-image-wrapper img:not([src]) + .icon-char-fallback {
  display: flex !important;
}

.icon-image-wrapper img[src=""],
.icon-image-wrapper img[src="null"],
.icon-image-wrapper img:not([src]) {
  display: none !important;
}

.icon-placeholder img[src=""] + .icon-fallback,
.icon-placeholder img[src="null"] + .icon-fallback,
.icon-placeholder img:not([src]) + .icon-fallback {
  display: flex !important;
}

.icon-placeholder img[src=""],
.icon-placeholder img[src="null"],
.icon-placeholder img:not([src]) {
  display: none !important;
}

/* 8. 排序样式 */
.sortable-ghost {
  opacity: 0.5;
  background-color: #f0f9ff !important;
}

.sortable-chosen {
  background-color: #e6f7ff !important;
  border-color: #91d5ff !important;
}

/* 9. 消息提示框层级 */
.el-message {
  z-index: 5000 !important;
}
</style>